import React, { useContext, useEffect, useState } from "react";
import RequireAuth from "../../shared/components/guards/RequireAuth";
import { AuthContext } from "../../contexts/AuthContext";
import { PageTitle } from "../../shared/components/styled/Typography.styled";
import ActionSnackbar from "../../shared/components/ActionSnackbar";
import { NO_LOAN_REGISTERED_WARNING_MESSAGE } from "../../shared/constants/UserProfile.constants";
import { Box, Paper, Tooltip, useTheme, useMediaQuery, Button } from "@mui/material";
import {
  AsideNavigation,
  FlexContainer,
  RelativeContainer,
} from "../../shared/components/styled/Container.styled";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import Statements from "./statements/Statements";
import {
  BANNER_IMAGE_STATEMENTS_KEY,
  BANNER_URL_STATEMENTS_KEY,
  DOCUMENTS_ALLOW_ACCESS_KEY,
  DOCUMENTS_ALLOW_REQUEST_PAYOFF_KEY,
  DOCUMENTS_ALLOW_UPLOAD_KEY,
} from "../../shared/constants/Configurator.constants";
import MarketingBanner from "../../shared/components/configurator/MarketingBanner";
import LoanDetailsExpandable from "../../shared/components/LoanDetailsExpandable";
import { ConfiguratorContext } from "../../contexts/ConfiguratorContext";
import {
  REQUEST_PAYOFF_NOT_ALLOWED_MESSAGE,
  UPLOAD_NOT_ALLOWED,
  VIEW_DOCUMENT_NOT_ALLOWED_MESSAGE,
} from "../../shared/constants/StatementsDocuments.constants";
import { findInArray, isAllowed } from "../../shared/utils/utility-functions";
import { useResponsivePadding } from "../../shared/hooks/useResponsivePadding";
import PageLayout from "../../shared/components/PageLayout";
import HorizontalMobileNavigation from "../../shared/components/HorizontalMobileNavigation";

const StatementsAndDocuments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeLoan, activeLoanDetails, token } = useContext(AuthContext);
  const { edittedFields } = useContext(ConfiguratorContext);
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [noLoanSnackbarOpen, setNoLoanSnackbarOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { bannerContainer, contentContainer } = useResponsivePadding();
  const inActiveOrDeadLoan =
    activeLoanDetails?.loanDetails?.inActiveIndicator ||
    activeLoanDetails?.loanDetails?.deadLoanIndicator;
  const configuratorDocumentsAllowField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOW_ACCESS_KEY
  );
  const configuratorDocumentsUploadAllowField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOW_UPLOAD_KEY
  );
  const configuratorPayoffQuoteAllowField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOW_REQUEST_PAYOFF_KEY
  );

  const canViewDocuments = isAllowed(
    configuratorDocumentsAllowField,
    "value",
    "true",
    true
  );
  const canUploadDocuments = isAllowed(
    configuratorDocumentsUploadAllowField,
    "value",
    "true",
    true
  );
  const canPayoffQuote = isAllowed(
    configuratorPayoffQuoteAllowField,
    "value",
    "true",
    true
  );

  useEffect(() => {
    if (
      !canViewDocuments &&
      (location.pathname === "/statements-documents" ||
        location.pathname === "/statements-documents/")
    ) {
      if (canUploadDocuments) {
        navigate("/statements-documents/upload", { replace: true });
      } else if (canPayoffQuote) {
        navigate("/statements-documents/request", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewDocuments, canUploadDocuments, canPayoffQuote, location]);

  useEffect(() => {
    if (token && activeLoan === "no-loan") {
      setNoLoanSnackbarOpen(true);
    } else {
      setNoLoanSnackbarOpen(false);
    }
  }, [token, activeLoan]);

  const handleNoLoanSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setNoLoanSnackbarOpen(false);
  };

  return (
    <RequireAuth>
      <RelativeContainer width="100%">
        <PageTitle
          variant="h1"
          component="h1"
          color="primary.dark"
          fontSize="3rem"
          display={isMobile ? "none" : "block"}
          sx={{
            width: {
              md: 'calc(100% - 24px - 500px)',
              lg: '100%',
            }
          }}
        >
          Statements & Documents
        </PageTitle>
        {!isMobile && <LoanDetailsExpandable />}
      </RelativeContainer>
      <Paper
        elevation={0}
        sx={{ backgroundColor: { xs: "unset", md: "white" } }}
      >
        <PageLayout>
          <FlexContainer
            justifycontent="space-between"
            alignitems="center"
            width="100%"
            sx={{ marginBottom: isMobile ? "20px" : 0 }}
          >
            <PageTitle
              variant="h1"
              component="h1"
              color="primary.black"
              fontSize="2rem"
              display={isMobile ? "block" : "none"}
              padding="0 !important"
              // width="20%"
            >
              Statements & Documents
            </PageTitle>

            {isMobile && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowLoanDetails(!showLoanDetails)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  borderColor: "primary.main",
                  color: "primary.main",
                  fontSize: "0.875rem",
                  padding: "8px 16px",
                  minWidth: "140px",
                  "&:hover": {
                    borderColor: "primary.dark",
                    color: "primary.dark",
                  },
                }}
              >
                {showLoanDetails ? "Hide Details" : "View Loan Details"}
              </Button>
            )}
          </FlexContainer>

            {isMobile && showLoanDetails && (
            <Box sx={{ 
              marginBottom: "20px",
              background: "#ffe8ba",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
            }}>
              <LoanDetailsExpandable expanded={true} />
            </Box>
          )}

          <FlexContainer
            alignitems="flex-start"
            sx={{ flexDirection: { xs: "column", md: "row" } }}
          >
            {isMobile ? (
              <HorizontalMobileNavigation
                items={[
                  {
                    to: "/statements-documents",
                    label: "Statements",
                    linkSx: {
                      pointerEvents: !canViewDocuments ? "none" : "auto",
                      opacity: !canViewDocuments ? 0.5 : 1,
                    },
                  },
                  {
                    to: "tax",
                    label: "Tax Documents",
                    linkSx: {
                      pointerEvents: !canViewDocuments ? "none" : "auto",
                      opacity: !canViewDocuments ? 0.5 : 1,
                    },
                  },
                  {
                    to: "other",
                    label: "Other Documents",
                    linkSx: {
                      pointerEvents: !canViewDocuments ? "none" : "auto",
                      opacity: !canViewDocuments ? 0.5 : 1,
                    },
                  },
                  ...(!inActiveOrDeadLoan
                    ? [
                        {
                          to: "request",
                          label: "Request Payoff",
                          linkSx: {
                            pointerEvents: !canPayoffQuote ? "none" : "auto",
                            opacity: !canPayoffQuote ? 0.5 : 1,
                          },
                        },
                      ]
                    : []),
                  ...(!inActiveOrDeadLoan
                    ? [
                        {
                          to: "upload",
                          label: "Upload Documents",
                          linkSx: {
                            pointerEvents: !canUploadDocuments
                              ? "none"
                              : "auto",
                            opacity: !canUploadDocuments ? 0.5 : 1,
                          },
                        },
                      ]
                    : []),
                ]}
              />
            ) : (
              // Desktop vertical navigation
              <AsideNavigation>
                <Tooltip
                  title={
                    !canViewDocuments ? VIEW_DOCUMENT_NOT_ALLOWED_MESSAGE : ""
                  }
                >
                  <Box
                    sx={{
                      cursor: !canViewDocuments ? "not-allowed" : "inherit",
                      lineHeight: 1.286,
                    }}
                  >
                    <Link
                      to="/statements-documents"
                      className={
                        !canViewDocuments
                          ? "disabled"
                          : location.pathname === "/statements-documents" ||
                            location.pathname === "/statements-documents/"
                          ? "active"
                          : ""
                      }
                    >
                      Statements
                    </Link>
                  </Box>
                </Tooltip>
                <Tooltip
                  title={
                    !canViewDocuments ? VIEW_DOCUMENT_NOT_ALLOWED_MESSAGE : ""
                  }
                >
                  <Box
                    sx={{
                      cursor: !canViewDocuments ? "not-allowed" : "inherit",
                      lineHeight: 1.286,
                    }}
                  >
                    <Link
                      to="tax"
                      className={
                        !canViewDocuments
                          ? "disabled"
                          : location.pathname.includes(
                              "/statements-documents/tax"
                            )
                          ? "active"
                          : ""
                      }
                    >
                      Tax Documents
                    </Link>
                  </Box>
                </Tooltip>
                <Tooltip
                  title={
                    !canViewDocuments ? VIEW_DOCUMENT_NOT_ALLOWED_MESSAGE : ""
                  }
                >
                  <Box
                    sx={{
                      cursor: !canViewDocuments ? "not-allowed" : "inherit",
                      lineHeight: 1.286,
                    }}
                  >
                    <Link
                      to="other"
                      className={
                        !canViewDocuments
                          ? "disabled"
                          : location.pathname.includes(
                              "/statements-documents/other"
                            )
                          ? "active"
                          : ""
                      }
                    >
                      Other Documents
                    </Link>
                  </Box>
                </Tooltip>
                <Tooltip
                  title={
                    !canPayoffQuote ? REQUEST_PAYOFF_NOT_ALLOWED_MESSAGE : ""
                  }
                >
                  <Box
                    sx={{
                      cursor: !canPayoffQuote ? "not-allowed" : "inherit",
                      lineHeight: 1.286,
                    }}
                  >
                    {!inActiveOrDeadLoan ? (
                      <Link
                        to="request"
                        className={
                          !canPayoffQuote
                            ? "disabled"
                            : location.pathname.includes(
                                "/statements-documents/request"
                              )
                            ? "active"
                            : ""
                        }
                      >
                        Request Payoff Quote
                      </Link>
                    ) : (
                      ""
                    )}
                  </Box>
                </Tooltip>
                <Tooltip title={!canUploadDocuments ? UPLOAD_NOT_ALLOWED : ""}>
                  <Box
                    sx={{
                      cursor: !canUploadDocuments ? "not-allowed" : "inherit",
                      lineHeight: 1.286,
                    }}
                  >
                    {!inActiveOrDeadLoan ? (
                      <Link
                        to="upload"
                        className={
                          !canUploadDocuments
                            ? "disabled"
                            : location.pathname.includes(
                                "/statements-documents/upload"
                              )
                            ? "active"
                            : ""
                        }
                      >
                        Upload Documents
                      </Link>
                    ) : (
                      ""
                    )}
                  </Box>
                </Tooltip>
              </AsideNavigation>
            )}
            <FlexContainer padding={contentContainer} width="100%">
              <FlexContainer width="100%" flexdirection="column" gap="25px">
                {location.pathname === "/statements-documents" ||
                location.pathname === "/statements-documents/" ? (
                  <Statements />
                ) : (
                  <Outlet />
                )}
              </FlexContainer>
            </FlexContainer>
          </FlexContainer>
        </PageLayout>
        <FlexContainer padding={bannerContainer} alignitems="flex-start">
          <MarketingBanner
            elementKey={BANNER_IMAGE_STATEMENTS_KEY}
            hyperlinkElementKey={BANNER_URL_STATEMENTS_KEY}
          />
        </FlexContainer>
      </Paper>
      <ActionSnackbar
        severity="warning"
        borderColor={theme.palette.warning.main}
        vertical="top"
        horizontal="center"
        width="50%"
        isOpen={noLoanSnackbarOpen}
        closeFn={handleNoLoanSnackbarClose}
        snackbarLabel={NO_LOAN_REGISTERED_WARNING_MESSAGE}
        actionLabel="Register Loan"
        actionFn={() => navigate("/profile/loan")}
      />
    </RequireAuth>
  );
};

export default StatementsAndDocuments;